<template>
  <div
    class="carried-selection-notice d-flex flex-wrap align-items-center gap-3 rounded-3 px-3 py-2 mb-3 bg-primary-subtle border border-primary-subtle small"
  >
    <FontAwesomeIcon icon="arrow-right" class="text-primary flex-shrink-0" />
    <span class="flex-grow-1">
      <strong>{{ summaryText }}</strong>
      {{ $t("go_cancel.carried_selection_paused") }}
    </span>
    <button
      v-if="canUseAmount"
      type="button"
      class="btn btn-sm btn-outline-primary flex-shrink-0"
      @click="$emit('use:amount', carried.mwh)"
    >
      {{
        $t("go_cancel.carried_selection_use_amount", {
          mwh: formatNumberWithSpaces(carried.mwh),
        })
      }}
    </button>
    <button
      type="button"
      class="btn btn-sm btn-link p-0 fw-semibold flex-shrink-0"
      @click="$emit('clear')"
    >
      {{ $t("go_cancel.carried_selection_clear") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * A selection that arrived in the URL (Overview ▸ Action ▸ Cancel), frozen on arrival: the notice
 * describes what was carried over, not the live selection the operator goes on to edit.
 *
 * @property partial - Rows carried with only part of their amount selected
 */
export interface CarriedSelection {
  rows: number;
  mwh: number;
  partial: number;
}

const props = defineProps<{
  carried: CarriedSelection;
  canUseAmount: boolean;
}>();

defineEmits<{
  "use:amount": [mwh: number];
  clear: [];
}>();

const { t } = useI18n();

const summaryText = computed(() => {
  const { rows, mwh, partial } = props.carried;
  const formattedMwh = formatNumberWithSpaces(mwh);
  if (partial === 0) {
    return t(
      "go_cancel.carried_selection_summary",
      { mwh: formattedMwh },
      rows
    );
  }
  return t(
    "go_cancel.carried_selection_summary_partial",
    {
      mwh: formattedMwh,
      partial: t("go_cancel.carried_selection_partial", partial),
    },
    rows
  );
});
</script>
